/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import classNames from 'classnames/bind'
import { Field } from 'redux-form'
import { API_TYPES } from 'constants/apiTypes'
import { MAX_LENGTHS, BILLING_FIELD_NAMES } from 'constants/forms'
import { Text, GetTextPath } from 'hocs/localization'
import { Input, Select } from 'apps/FormFields'
import { correctedBillingAddressModal } from 'apps/Profile/CorrectedAddressModal'
import Button from 'elements/Button'
import {
  createValidation,
  createValidator,
  hasRequired,
  hasSelectedValue,
  hasValidPostalCode,
  onBeforeChangePostalCode,
} from 'utils/form'
import * as styles from './BillingInfoForm.scss'

const cx = classNames.bind(styles)

interface IState {
  isDisabled
}

interface IProps {
  isDisabled?: boolean,
  t: Text,
  getTextPath: GetTextPath,
  handleSubmit: any,
  submitting: boolean,
  valid: boolean,
  dirty: boolean,
  isView: boolean,
  stateOptions: any[],
  onChangeCountry: () => void,
  isCardUpdate: boolean,
  paymentInfoUrl: string,
  isEdit: boolean,
  onConfirmCorrectedAddress: () => void,
  isUserUnableToUpdateInfo: boolean,
  postalCode: string,
}

class BillingInfoForm extends React.Component<IProps, IState> {
  public readonly state = {
    isDisabled: false,
  }

  private requiredFieldValidation = createValidation([
    createValidator(hasRequired, this.props.getTextPath('errors.textField')),
  ])

  private selectFieldValidation = createValidation([
    createValidator(hasSelectedValue, this.props.getTextPath('errors.select')),
  ])

  private postalCodeValidation = createValidation([
    createValidator(hasValidPostalCode, this.props.getTextPath('errors.postalCode')),
  ])

  public componentWillReceiveProps (nextProps) {
    if (this.props.isDisabled !== nextProps.isDisabled) {
      this.setState({
        isDisabled: nextProps.isDisabled,
      })
    }
  }

  public render () {
    const {
      t,
      handleSubmit,
      stateOptions,
      isEdit,
      onConfirmCorrectedAddress,
      submitting,
      isUserUnableToUpdateInfo,
      postalCode,
    } = this.props

    const isFieldDisabled = (isEdit && isUserUnableToUpdateInfo) || this.state.isDisabled || submitting

    return (
      <form className={styles.root} onSubmit={handleSubmit}>
        <div className={cx(styles.fieldsContainer, styles.row)}>
          <div className={styles.fieldWrap}>
            <Field
              label={t('address1Label')}
              type='text'
              name={BILLING_FIELD_NAMES.address1}
              placeholder={t('address1Placeholder')}
              component={Input}
              maxLength={MAX_LENGTHS.address}
              validate={this.requiredFieldValidation}
              hint={t('address1Hint')}
              size='large'
              disabled={isFieldDisabled}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <Field
              id='state'
              type='text'
              name={BILLING_FIELD_NAMES.stateId}
              component={Select}
              placeholder={t('statePlaceholder')}
              validate={this.selectFieldValidation}
              disabled={isFieldDisabled}
              options={stateOptions}
              label={t('stateLabel')}
              apiTypes={[API_TYPES.states]}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <Field
              type='text'
              name={BILLING_FIELD_NAMES.city}
              component={Input}
              maxLength={MAX_LENGTHS.city}
              validate={this.requiredFieldValidation}
              disabled={isFieldDisabled}
              label={t('cityLabel')}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <div className={cx('postalCode')}>
              <Field
                type='text'
                name={BILLING_FIELD_NAMES.postalCode}
                component={Input}
                validate={[this.requiredFieldValidation, this.postalCodeValidation]}
                disabled={isFieldDisabled}
                label={t('postalCodeLabel')}
                mask={
                  postalCode.length === 0 || postalCode.length > 5
                    ? t('postalCodeMaskFull')
                    : t('postalCodeMaskShort')
                }
                maskChar={null}
                beforeMaskedValueChange={onBeforeChangePostalCode}
                required
              />
            </div>
          </div>
        </div>
        {isEdit && !isUserUnableToUpdateInfo && (
          <div className={styles.submitButton}>
            <Button fluid disabled={submitting}>
              {submitting ? t('saving') : t('saveChanges')}
            </Button>
            <correctedBillingAddressModal.Component onConfirm={onConfirmCorrectedAddress} />
          </div>
        )}
      </form>
    )
  }
}

export default BillingInfoForm
